// Single source of truth for the Tenant Portal Ledger column sets.
//
// Each ledger tab renders its own on-screen table, so the export
// pipeline (ledger-export.js → CSV + PDF) mirrors each of them with a
// dedicated column schema:
//   • Full Ledger → TPLedgerTable  : Date, Type, Source, Charges /
//                                     Payments, [Sales Tax], Balance
//   • Payments    → TPPaymentsTable: Date, Method, Person, Amount
//   • Charges     → TPChargesTable : Date, Description, Amount
//   • Invoices    → TPInvoicesTable: Invoice Number, Invoice Date,
//                                     Amount, Tax, Invoice Total,
//                                     Unpaid Total, Status
//
// The Sales Tax column on the Full Ledger is conditional (it only shows
// when the property is tax-exclusive, the backend flags tax transactions,
// or any row carries tax data) — exactly the TPLedgerTable rule — so
// getColumnsForScope takes a `showSalesTax` flag for the Full scope.
//
// Each column is a frozen descriptor with:
//   • id              — stable identifier, never localised
//   • header()        — () => localised header string (factory so the
//                       active locale is resolved at call time)
//   • align           — 'left' | 'right'
//   • formatForExport — (row) => primitive cell value safe for CSV / PDF

import { LEDGER_SCOPES } from 'portal-modules/ledger/utils/ledger-scope'
import { LEDGER_TRANSACTION_TYPES } from 'portal-modules/payments/constants/types'
import getLedgerWithTaxTotalAmount from 'portal-modules/ledger/utils/get-ledger-with-tax-total-amount'

const isPaymentRow = row =>
  row?.transaction_type === LEDGER_TRANSACTION_TYPES.PAYMENT.value

const isChargeRow = row =>
  row?.transaction_type === LEDGER_TRANSACTION_TYPES.CHARGE.value

const formatLocaleDate = value => {
  if (!value) return ''
  if (
    typeof i18nUtils !== 'undefined' &&
    typeof i18nUtils.datetime === 'function'
  ) {
    return i18nUtils.getLocale && i18nUtils.getLocale() === 'pt-BR'
      ? i18nUtils.datetime(value, 'DATE_ALPHA_LONG')
      : i18nUtils.datetime(value, 'MMM D, YYYY')
  }
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString()
}

const formatCurrency = value => {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  if (
    typeof i18nUtils !== 'undefined' &&
    typeof i18nUtils.currency === 'function'
  ) {
    return i18nUtils.currency(n)
  }
  return n.toFixed(2)
}

// ── Shared cell formatters ──────────────────────────────────────────

// Mirrors TPLedgerTable: "Charge - Rent", or plain label for Payment.
const formatTypeLabel = row => {
  if (!row) return ''
  const type = row.type ?? ''
  const label = row.label ?? ''
  if (type === 'Payment') return label || type
  if (type && label) return `${type} - ${label}`
  return type || label
}

// TPLedgerTable renders `description || 'N/A'` in the Source column.
const formatSource = row => {
  const description = row?.description
  return description === null || description === undefined || description === ''
    ? 'N/A'
    : description
}

// Sign derived from transaction_type — matches the on-screen TPLedgerTable
// Charges / Payments column. Unknown transaction_type falls through to the
// raw signed amount so credits / returns are not silently misclassified.
const formatChargesPayments = row => {
  if (row?.amount === null || row?.amount === undefined || row?.amount === '') {
    return ''
  }
  const n = Number(row.amount)
  if (!Number.isFinite(n)) return ''
  const abs = formatCurrency(Math.abs(n))
  if (isPaymentRow(row)) return `-${abs}`
  if (isChargeRow(row)) return `+${abs}`
  return n < 0 ? `-${abs}` : abs
}

// Sales Tax cell — the absolute tax total for the row, matching
// TPLedgerTable's `currency(Math.abs(taxTotalAmount))`.
const formatSalesTax = row => {
  const taxes = Array.isArray(row?.taxes) ? row.taxes : []
  const { taxTotalAmount } = getLedgerWithTaxTotalAmount(
    taxes,
    row?.amount ?? 0,
  )
  return formatCurrency(Math.abs(Number(taxTotalAmount) || 0))
}

// Accounting parens-style negative — Excel + Numbers both parse "(x)"
// as a negative numeric. Explicit null/undefined/empty guard so missing
// balances render blank, not $0.00.
const formatBalance = row => {
  if (
    row?.balance === null ||
    row?.balance === undefined ||
    row?.balance === ''
  ) {
    return ''
  }
  const n = Number(row.balance)
  if (!Number.isFinite(n)) return ''
  if (row.is_negative_balance) return `(${formatCurrency(Math.abs(n))})`
  return formatCurrency(n)
}

// TPPaymentsTable Method column: `label.split('-')[1] || label`.
const formatPaymentMethod = row => {
  const label = row?.label
  if (label === null || label === undefined) return ''
  const parts = String(label).split('-')
  return (parts[1] !== undefined ? parts[1] : label) || label
}

const formatPaymentPerson = row => row?.description ?? ''

const formatPaymentAmount = row => formatCurrency(row?.amount)

// TPChargesTable Description column: `label || description`.
const formatChargeDescription = row => row?.label || row?.description || ''

// TPChargesTable Amount column: tax-inclusive ledger amount.
const formatChargeAmount = row => {
  const taxes = Array.isArray(row?.taxes) ? row.taxes : []
  const { ledgerAmount } = getLedgerWithTaxTotalAmount(taxes, row?.amount ?? 0)
  return formatCurrency(ledgerAmount)
}

// Invoice money cells mirror TPInvoicesTable, which always renders
// `i18nUtils.currency(value)` — a missing / zero value shows "$0.00" on
// screen (see the Tax / Unpaid Total columns), so the export does the same
// rather than blanking the cell.
const formatInvoiceCurrency = value => formatCurrency(Number(value) || 0)

// TPInvoicesTable Invoice Total: negative balances in accounting parens.
const formatInvoiceTotal = row => {
  const value = Number(row?.total_invoice_amount) || 0
  if (row?.is_negative_balance) return `(${formatCurrency(Math.abs(value))})`
  return formatCurrency(value)
}

const freezeColumn = column => Object.freeze({ ...column })

const DATE_COLUMN = freezeColumn({
  id: 'date',
  header: () => __('Date'),
  align: 'left',
  formatForExport: row => formatLocaleDate(row?.date),
})

// ── Full Ledger schema (mirrors TPLedgerTable) ──────────────────────

const FULL_TYPE_COLUMN = freezeColumn({
  id: 'type',
  header: () => __('Type'),
  align: 'left',
  formatForExport: row => formatTypeLabel(row),
})

const FULL_SOURCE_COLUMN = freezeColumn({
  id: 'source',
  header: () => __('Source'),
  align: 'left',
  formatForExport: row => formatSource(row),
})

const FULL_CHARGES_PAYMENTS_COLUMN = freezeColumn({
  id: 'chargesPayments',
  header: () => __('Charges / Payments'),
  align: 'right',
  formatForExport: row => formatChargesPayments(row),
})

const FULL_SALES_TAX_COLUMN = freezeColumn({
  id: 'salesTax',
  header: () => __('Sales Tax'),
  align: 'right',
  formatForExport: row => formatSalesTax(row),
})

const FULL_BALANCE_COLUMN = freezeColumn({
  id: 'balance',
  header: () => __('Balance'),
  align: 'right',
  formatForExport: row => formatBalance(row),
})

// Full Ledger without the conditional Sales Tax column.
export const TP_LEDGER_COLUMNS = Object.freeze([
  DATE_COLUMN,
  FULL_TYPE_COLUMN,
  FULL_SOURCE_COLUMN,
  FULL_CHARGES_PAYMENTS_COLUMN,
  FULL_BALANCE_COLUMN,
])

// Full Ledger with the Sales Tax column (inserted before Balance, exactly
// where TPLedgerTable renders it).
export const TP_LEDGER_COLUMNS_WITH_TAX = Object.freeze([
  DATE_COLUMN,
  FULL_TYPE_COLUMN,
  FULL_SOURCE_COLUMN,
  FULL_CHARGES_PAYMENTS_COLUMN,
  FULL_SALES_TAX_COLUMN,
  FULL_BALANCE_COLUMN,
])

// ── Payments schema (mirrors TPPaymentsTable) ───────────────────────

export const TP_PAYMENTS_COLUMNS = Object.freeze([
  DATE_COLUMN,
  freezeColumn({
    id: 'method',
    header: () => __('Method'),
    align: 'left',
    formatForExport: row => formatPaymentMethod(row),
  }),
  freezeColumn({
    id: 'person',
    header: () => __('Person'),
    align: 'left',
    formatForExport: row => formatPaymentPerson(row),
  }),
  freezeColumn({
    id: 'amount',
    header: () => __('Amount'),
    align: 'right',
    formatForExport: row => formatPaymentAmount(row),
  }),
])

// ── Charges schema (mirrors TPChargesTable) ─────────────────────────

export const TP_CHARGES_COLUMNS = Object.freeze([
  DATE_COLUMN,
  freezeColumn({
    id: 'description',
    header: () => __('Description'),
    align: 'left',
    formatForExport: row => formatChargeDescription(row),
  }),
  freezeColumn({
    id: 'amount',
    header: () => __('Amount'),
    align: 'right',
    formatForExport: row => formatChargeAmount(row),
  }),
])

// ── Invoices schema (mirrors TPInvoicesTable / customerInvoice store) ─

export const TP_INVOICE_COLUMNS = Object.freeze([
  freezeColumn({
    id: 'invoice_number',
    header: () => __('Invoice Number'),
    align: 'left',
    formatForExport: row => row?.invoice_number ?? '',
  }),
  freezeColumn({
    id: 'invoice_date',
    header: () => __('Invoice Date'),
    align: 'left',
    formatForExport: row => formatLocaleDate(row?.date),
  }),
  freezeColumn({
    id: 'amount',
    header: () => __('Amount'),
    align: 'right',
    formatForExport: row => formatInvoiceCurrency(row?.amount),
  }),
  freezeColumn({
    id: 'tax',
    header: () => __('Tax'),
    align: 'right',
    formatForExport: row => formatInvoiceCurrency(row?.total_taxes),
  }),
  freezeColumn({
    id: 'invoice_total',
    header: () => __('Invoice Total'),
    align: 'right',
    formatForExport: row => formatInvoiceTotal(row),
  }),
  freezeColumn({
    id: 'unpaid_total',
    header: () => __('Unpaid Total'),
    align: 'right',
    formatForExport: row => formatInvoiceCurrency(row?.unpaid_total),
  }),
  freezeColumn({
    id: 'status',
    header: () => __('Status'),
    align: 'left',
    formatForExport: row => row?.status ?? '',
  }),
])

// Base schema per scope (Full uses the no-tax variant here; the tax-aware
// variant is selected by getColumnsForScope when showSalesTax is set).
export const COLUMNS_BY_SCOPE = Object.freeze({
  [LEDGER_SCOPES.FULL]: TP_LEDGER_COLUMNS,
  [LEDGER_SCOPES.PAYMENTS]: TP_PAYMENTS_COLUMNS,
  [LEDGER_SCOPES.CHARGES]: TP_CHARGES_COLUMNS,
  [LEDGER_SCOPES.INVOICES]: TP_INVOICE_COLUMNS,
})

// Defensive lookup: unknown / undefined scopes fall back to the Full
// Ledger schema so an export never silently emits an empty schema and a
// future scope name typo never throws. The Full Ledger gains its Sales
// Tax column only when `showSalesTax` is true (same rule as TPLedgerTable).
export const getColumnsForScope = (scope, { showSalesTax = false } = {}) => {
  if (scope === LEDGER_SCOPES.FULL || !COLUMNS_BY_SCOPE[scope]) {
    return showSalesTax ? TP_LEDGER_COLUMNS_WITH_TAX : TP_LEDGER_COLUMNS
  }
  return COLUMNS_BY_SCOPE[scope]
}
